/**
 * `useHeroPalette` — React hook that extracts a dominant-color
 * palette from a 16:9 hero image URL using Google's Material
 * Color Utilities (Apache-2.0 licensed library, commercial-use
 * friendly).
 *
 * The hook loads the image into an off-screen canvas, quantizes
 * the pixel data via the `Celebi` quantizer, scores the results
 * via `Score`, then derives three CSS-ready values:
 *
 *   - `--hero-dominant` — the strongest color pulled from the image.
 *   - `--hero-accent`   — the Hct-rotated secondary that pairs
 *     with the dominant.
 *   - `--hero-text`     — `'light'` or `'dark'`, driving the overlay
 *     text color against the hero image.
 *
 * Palette extraction runs ONCE per image URL (keyed by URL) and
 * is skipped when the URL is empty / undefined. The hook returns
 * `undefined` while the extraction is in-flight so the caller can
 * fall back to neutral styling on initial render.
 *
 * Why client-side: avoids shipping `sharp`/`jimp` to Lambda and
 * means pre-existing hero images get palettized on next SPA load
 * without a server re-enrichment.
 *
 * Function-length budget: every function stays ≤ 20 non-comment
 * lines per R3.7.
 */

import { useEffect, useState } from 'react';

/** Shape returned by {@link useHeroPalette}. */
export interface HeroPalette {
  readonly dominant: string;    // "#rrggbb"
  readonly accent: string;      // "#rrggbb"
  readonly textOnHero: 'light' | 'dark';
}

/** Dimensions the hook samples the image down to before quantizing. */
const SAMPLE_WIDTH = 128;
const SAMPLE_HEIGHT = 72;

/**
 * Extract a 3-color palette from an image URL. Returns undefined
 * while the extraction is in flight or when `imageUrl` is empty.
 */
export function useHeroPalette(imageUrl: string | undefined): HeroPalette | undefined {
  const [palette, setPalette] = useState<HeroPalette | undefined>(undefined);
  useEffect(() => {
    if (imageUrl === undefined || imageUrl.length === 0) {
      setPalette(undefined);
      return;
    }
    let cancelled = false;
    extractPalette(imageUrl).then((p) => { if (!cancelled) setPalette(p); });
    return () => { cancelled = true; };
  }, [imageUrl]);
  return palette;
}

/**
 * Core extraction pipeline. Load the image into a canvas, pull
 * RGBA pixels, quantize via Celebi, score via `Score`, convert the
 * top result + its complement to hex, pick text contrast off the
 * dominant's tone. Returns undefined on any decode / network
 * failure so the caller falls back to neutral styling.
 */
async function extractPalette(url: string): Promise<HeroPalette | undefined> {
  const pixels = await readPixels(url);
  if (pixels === undefined || pixels.length === 0) return undefined;
  // Dynamically import material-color-utilities so SSR / vitest's
  // node resolver don't pre-load it (the lib's internal path imports
  // trip ESM resolution outside a bundler environment). In the SPA
  // bundle this lazy-loads a small chunk on first hero render.
  const mcu = await import('@material/material-color-utilities');
  const quantized = mcu.QuantizerCelebi.quantize(pixels as number[], 16);
  const ranked = mcu.Score.score(quantized);
  if (ranked.length === 0) return undefined;
  const dominantArgb = ranked[0];
  const accentArgb = complementaryArgb(mcu, dominantArgb);
  return {
    dominant: mcu.hexFromArgb(dominantArgb),
    accent: mcu.hexFromArgb(accentArgb),
    textOnHero: mcu.Hct.fromInt(dominantArgb).tone > 60 ? 'dark' : 'light',
  };
}

type McuModule = typeof import('@material/material-color-utilities');

/** Rotate the dominant's hue by ~140° in HCT space to produce a complementary accent. */
function complementaryArgb(mcu: McuModule, argb: number): number {
  const hct = mcu.Hct.fromInt(argb);
  const nextHue = (hct.hue + 140) % 360;
  return mcu.Hct.from(nextHue, Math.min(hct.chroma, 48), Math.max(hct.tone, 50)).toInt();
}

/**
 * Download the image, draw it onto a 128×72 off-screen canvas,
 * and read back the pixel data as an array of ARGB integers.
 * Returns undefined on CORS / network / decode failure.
 */
async function readPixels(url: string): Promise<ReadonlyArray<number> | undefined> {
  const img = await loadImage(url);
  if (img === undefined) return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_WIDTH;
  canvas.height = SAMPLE_HEIGHT;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return undefined;
  ctx.drawImage(img, 0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
  try {
    const data = ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT).data;
    return rgbaToArgb(data);
  } catch {
    return undefined;
  }
}

/** Convert a browser Uint8ClampedArray of RGBA pixels to an array of ARGB ints. */
function rgbaToArgb(rgba: Uint8ClampedArray): number[] {
  const argb: number[] = [];
  for (let i = 0; i < rgba.length; i += 4) {
    const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2], a = rgba[i + 3];
    if (a < 128) continue;
    // Pack as 0xAARRGGBB — same layout `argbFromRgb` produces.
    argb.push(((0xff << 24) | (r << 16) | (g << 8) | b) >>> 0);
  }
  return argb;
}

/** Load an image with CORS so getImageData is permitted. */
function loadImage(url: string): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(undefined);
    img.src = url;
  });
}
